import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { SponsorBanner } from '../components/SponsorBanner';
import { catsAPI, instagramAPI } from '../services/api';

// Component for Kittens Available Section
const KittensAvailableSection = ({ language, navigate }) => {
  const [kittens, setKittens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKittens();
  }, []);

  const fetchKittens = async () => {
    try {
      const data = await catsAPI.getByCategory('kitten');
      setKittens(data);
    } catch (error) {
      console.error('Error fetching kittens:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-600">
            {language === 'pt' ? 'Carregando...' : 'Loading...'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (kittens.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-brown-900">
            {language === 'pt' ? 'Filhotes Disponíveis' : 'Available Kittens'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 text-center py-8">
            {language === 'pt' 
              ? 'Nenhum filhote disponível no momento.'
              : 'No kittens available at the moment.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl text-brown-900">
          {language === 'pt' ? 'Filhotes Disponíveis' : 'Available Kittens'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Kittens Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {kittens.map((kitten) => (
                <button
                  key={kitten.id}
                  onClick={() => navigate('/cats?tab=kittens')}
                  className="group cursor-pointer"
                >
                  <div className="overflow-hidden rounded-lg mb-2 shadow-md group-hover:shadow-xl transition-shadow">
                    <img
                      loading="lazy" decoding="async" src={kitten.mainImage}
                      alt={kitten.name}
                      className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-center font-semibold text-brown-900 group-hover:text-brown-700 transition-colors">
                    {kitten.name}
                  </p>
                </button>
              ))}
            </div>
            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/cats?tab=kittens')}
                className="px-6 py-2 bg-brown-900 text-white rounded-full hover:bg-brown-800 transition-all duration-200"
              >
                {language === 'pt' ? 'Ver Mais Detalhes' : 'View More Details'}
              </button>
            </div>
          </div>

          {/* Pet Passport */}
          <div className="lg:w-1/4 flex-shrink-0">
            <button
              onClick={() => navigate('/cats?tab=kittens')}
              className="group cursor-pointer w-full"
            >
              <div className="rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow">
                <img
                  loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/4jdmei5r_pet-passaport.jpg"
                  alt={language === 'pt' ? 'Pet Passport' : 'Pet Passport'}
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Component for Gatil Mostacada Tab
const CatteryAboutTab = ({ language, t, navigate, setActiveTab }) => {
  const [cats, setCats] = useState([]);
  const [instagramPhotos, setInstagramPhotos] = useState([]);

  useEffect(() => {
    fetchCats();
    fetchInstagramPhotos();
  }, []);

  const fetchCats = async () => {
    try {
      const data = await catsAPI.getByCategory('stud');
      setCats(data);
    } catch (error) {
      console.error('Error fetching cats:', error);
    }
  };

  const fetchInstagramPhotos = async () => {
    try {
      const data = await instagramAPI.getFeed();
      if (data.success) {
        setInstagramPhotos(data.data.slice(0, 8)); // Get first 8 photos
      }
    } catch (error) {
      console.error('Error fetching Instagram feed:', error);
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Image - Same as Home page */}
      <div>
        <div className="rounded-2xl overflow-hidden shadow-2xl">
          <img
            loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_ocicat-cats/artifacts/5gpee1zy_Ocicat_0731_NOVA.jpg"
            alt={language === 'pt' ? 'Gatil Mostacada' : 'Gatil Mostacada'}
            className="w-full h-auto object-cover"
          />
        </div>
        <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
      </div>

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="mb-4">
            <h2 className="text-3xl font-bold text-brown-600 mb-2">{t.ourCats.catteryContent.title}</h2>
            <p className="text-xl text-brown-900">
              {t.ourCats.catteryContent.subtitle.split('**').map((part, i) => 
                i % 2 === 0 ? part : <strong key={i} className="font-bold">{part}</strong>
              )}
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-gray-600 leading-relaxed text-lg">
            {t.ourCats.catteryContent.sections[0].content.split('\n\n').map((paragraph, pIdx) => (
              <p key={pIdx} className="mb-4 last:mb-0">
                {paragraph.split('**').map((part, i) => 
                  i % 2 === 0 ? part : <strong key={i} className="font-bold text-brown-900">{part}</strong>
                )}
              </p>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Criação Humanizada Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-brown-900">{t.ourCats.catteryContent.sections[1].title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-gray-600 leading-relaxed text-lg">
            {t.ourCats.catteryContent.sections[1].content.split('\n\n').map((paragraph, pIdx) => (
              <p key={pIdx} className="mb-4 last:mb-0">
                {paragraph.split('**').map((part, i) => 
                  i % 2 === 0 ? part : <strong key={i} className="font-bold text-brown-900">{part}</strong>
                )}
              </p>
            ))}
          </div>
          {t.ourCats.catteryContent.sections[1].image && (
            <div className="mt-6 rounded-2xl overflow-hidden shadow-xl">
              <img
                loading="lazy" decoding="async" src={t.ourCats.catteryContent.sections[1].image}
                alt={t.ourCats.catteryContent.sections[1].title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Our Studs Section */}
      <div>
        <h2 className="text-3xl font-bold text-brown-600 mb-6 text-center">
          {language === 'pt' ? 'Nossos Padreadores' : 'Our Studs'}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {cats.map((cat) => (
            <button
              key={cat.id}
              onClick={() => navigate('/cats')}
              className="group cursor-pointer"
            >
              <div className="overflow-hidden rounded-lg mb-3 shadow-md group-hover:shadow-xl transition-shadow">
                <img
                  loading="lazy" decoding="async" src={cat.mainImage}
                  alt={cat.name}
                  className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <p className="text-center font-semibold text-brown-900 group-hover:text-brown-700 transition-colors">
                {cat.name}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Instagram Gallery Section */}
      {instagramPhotos.length > 0 && (
        <div>
          <h2 className="text-3xl font-bold text-brown-600 mb-6 text-center">
            {language === 'pt' ? 'Galeria de Fotos' : 'Photo Gallery'}
          </h2>
          <div className="text-center mb-6">
            <a 
              href="https://www.instagram.com/ocicat.brasil" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-brown-900 hover:text-brown-700 transition-colors font-medium"
            >
              @ocicat.brasil
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {instagramPhotos.map((photo) => (
              <a
                key={photo.id}
                href={photo.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square overflow-hidden rounded-lg hover:scale-105 transition-transform duration-300 shadow-md hover:shadow-xl"
              >
                <img
                  src={photo.media_type === 'VIDEO' ? (photo.thumbnail_url || photo.media_url) : photo.media_url}
                  alt={photo.caption || 'Ocicat'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
          <div className="text-center">
            <button
              onClick={() => navigate('/cats?tab=photos')}
              className="inline-block px-8 py-3 bg-brown-900 text-white rounded-full hover:bg-brown-800 transition-all duration-200 text-lg font-medium"
            >
              {language === 'pt' ? 'Ver Todas as Fotos' : 'View All Photos'}
            </button>
          </div>
        </div>
      )}

      {/* Ocicat Geracao Image */}
      <div className="mt-8">
        <div className="rounded-2xl overflow-hidden shadow-2xl">
          <img
            loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/xgjg1urj_ocicat-geracao.JPG"
            alt={language === 'pt' ? 'Ocicat Gerações' : 'Ocicat Generations'}
            className="w-full h-auto object-cover"
          />
        </div>
        <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
      </div>

      {/* CTA Section - Same as Home */}
      <div className="py-16 px-6 bg-brown-900 text-white rounded-2xl mt-8 text-center">
        <h2 className="text-4xl font-bold mb-6">
          {language === 'pt' ? 'Como ter um Ocicat?' : 'How to have an Ocicat?'}
        </h2>
        <p className="text-xl mb-8 text-gray-200">
          {language === 'pt' 
            ? 'Descubra como você pode fazer parte da família Ocicat Brasil'
            : 'Discover how you can become part of the Ocicat Brasil family'}
        </p>
        <button
          onClick={() => {
            setActiveTab('how');
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
          }}
          className="inline-block px-10 py-4 bg-white text-brown-900 rounded-full hover:bg-gray-100 transition-all duration-200 text-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          {language === 'pt' ? 'Saiba Mais' : 'Learn More'}
        </button>
      </div>
    </div>
  );
};

export const Cattery = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get tab from URL params
  const urlTab = searchParams.get('tab');
  
  const [activeTab, setActiveTab] = useState(urlTab || 'about');

  // Update active tab when URL changes
  useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab);
    } else {
      setActiveTab('about');
    }
  }, [urlTab]);

  const tabs = [
    { id: 'about', label: language === 'pt' ? 'Gatil Mostacada' : 'Gatil Mostacada' },
    { id: 'how', label: language === 'pt' ? 'Como ter um Ocicat' : 'How to have an Ocicat' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-semibold text-brown-600 mb-12 text-center">
            {language === 'pt' ? 'Nosso Gatil' : 'Our Cattery'}
          </h1>

          {/* Custom Tabs */}
          <div className="mb-12">
            <div className="border-b border-gray-200">
              <nav className="flex flex-wrap gap-4 -mb-px justify-center">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-2 px-4 border-b-2 font-medium text-base transition-colors ${
                      activeTab === tab.id
                        ? 'border-brown-900 text-brown-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Gatil Mostacada Tab */}
          {activeTab === 'about' && (
            <CatteryAboutTab language={language} t={t} navigate={navigate} setActiveTab={setActiveTab} />
          )}

          {/* Como ter um Ocicat Tab */}
          {activeTab === 'how' && (
            <div className="space-y-8">
              {/* Hero Image */}
              <div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/4wg5mh07_filhotes2.jpg"
                    alt={language === 'pt' ? 'Filhotes Ocicat' : 'Ocicat Kittens'}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
              </div>

              {/* Requisitos Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl text-brown-900">{t.ourCats.catteryContent.sections[2].title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-gray-600 leading-relaxed text-lg">
                    {t.ourCats.catteryContent.sections[2].content.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-4 last:mb-0">
                        {paragraph.split('**').map((part, i) => 
                          i % 2 === 0 ? part : <strong key={i} className="font-bold text-brown-900">{part}</strong>
                        )}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quando o filhote é disponibilizado Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl text-brown-900">{t.ourCats.catteryContent.sections[3].title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-gray-600 leading-relaxed text-lg">
                    {t.ourCats.catteryContent.sections[3].content.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-4 last:mb-0">
                        {paragraph.split('**').map((part, i) => 
                          i % 2 === 0 ? part : <strong key={i} className="font-bold text-brown-900">{part}</strong>
                        )}
                      </p>
                    ))}
                  </div>

                  {/* Items list */}
                  {t.ourCats.catteryContent.sections[3].items && (
                    <ul className="space-y-1 mt-4">
                      {t.ourCats.catteryContent.sections[3].items.map((item, itemIdx) => (
                        <li key={itemIdx} className="flex items-start gap-3">
                          <span className="text-brown-900 mt-1.5">•</span>
                          <span className="text-gray-700 flex-1">
                            {item.split('**').map((part, i) => 
                              i % 2 === 0 ? part : <strong key={i} className="font-bold text-brown-900">{part}</strong>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Footer */}
                  {t.ourCats.catteryContent.sections[3].footer && (
                    <p className="text-gray-600 italic mt-6 pt-4 border-t border-gray-200">
                      {t.ourCats.catteryContent.sections[3].footer.split('**').map((part, i) => 
                        i % 2 === 0 ? part : <strong key={i} className="font-bold text-brown-900">{part}</strong>
                      )}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Kittens Available Section */}
              <KittensAvailableSection language={language} navigate={navigate} />

              {/* Contact CTA Section */}
              <div className="mt-12 py-16 px-8 bg-brown-900 text-white rounded-2xl text-center">
                <h2 className="text-4xl font-bold mb-6">
                  {language === 'pt' ? 'Entre em contato conosco' : 'Contact us'}
                </h2>
                <p className="text-xl mb-8 text-gray-200">
                  {language === 'pt' 
                    ? 'Tem dúvidas ou quer saber mais sobre nossos ocicats? Estamos à disposição!'
                    : 'Have questions or want to know more about our ocicats? We\'re here to help!'}
                </p>
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-block px-10 py-4 bg-white text-brown-900 rounded-full hover:bg-gray-100 transition-all duration-200 text-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  {language === 'pt' ? 'Fale Conosco' : 'Contact Us'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <SponsorBanner language={language} />
      <Footer />
    </div>
  );
};